import React, { useState, useEffect, useRef } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Eye, Flame, TrendingUp, Calendar, Play, BarChart3, Instagram, Youtube } from 'lucide-react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';

/* ----------------------------------------------------------------------------
   DATA — public view counts via Apify, 2026-03-10 -> 2026-06-09.
   Instagram (@realkushdesai reels) + YouTube (@realkushdesai shorts).
   These are PUBLIC view/play counts, NOT private "reach" (an Insights-only metric).
   Raw: clients-current/kush/research/kush-reels-2026-06-10.json
        clients-current/kush/research/kush-youtube-2026-06-10.json
---------------------------------------------------------------------------- */

const IG = { posts: 80, views: 672763, likes: 29942, comments: 10078, avg: 8410, median: 1039, er: 5.9, hits: 5 };
const YT = { posts: 71, views: 362229, avg: 5102, median: 3189, hits: 0 };
const COMBINED = { posts: IG.posts + YT.posts, views: IG.views + YT.views };

const MONTHS = [
    { month: "Mar", label: "March '26", ig: 210350, yt: 116510, igReels: 18, ytVids: 18 },
    { month: "Apr", label: "April '26", ig: 58181, yt: 90034, igReels: 26, ytVids: 26 },
    { month: "May", label: "May '26", ig: 395370, yt: 143808, igReels: 29, ytVids: 20 },
    { month: "Jun", label: "June '26", ig: 8862, yt: 11877, igReels: 7, ytVids: 7, partial: true },
];

const IG_DISTRIBUTION = [
    { bucket: '50K+', sub: 'Breakout hits', count: 5, pct: 6, color: 'from-emerald-500 to-teal-400' },
    { bucket: '20K-50K', sub: 'Strong', count: 1, pct: 1, color: 'from-yellow-500 to-amber-400' },
    { bucket: '5K-20K', sub: 'Solid', count: 3, pct: 4, color: 'from-orange-500 to-amber-400' },
    { bucket: '1K-5K', sub: 'Steady', count: 32, pct: 40, color: 'from-blue-500 to-indigo-400' },
    { bucket: 'Under 1K', sub: 'Low travel', count: 39, pct: 49, color: 'from-slate-500 to-slate-400' },
];

const IG_TOP = [
    { hook: 'GTA 6 + Rockstar', views: '160,013', likes: '7,701', url: 'https://www.instagram.com/p/DYPRUAzBI6Q/', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', emoji: '🎮' },
    { hook: 'The Steppa app clone', views: '135,794', likes: '5,053', url: 'https://www.instagram.com/p/DYhRkE5B9Gp/', gradient: 'from-yellow-600 via-amber-500 to-orange-400', emoji: '🏃' },
    { hook: 'Your WiFi is spying', views: '108,768', likes: '6,647', url: 'https://www.instagram.com/p/DV00WMEjdVF/', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', emoji: '📡' },
    { hook: 'The fake Spotify band', views: '78,723', likes: '5,042', url: 'https://www.instagram.com/p/DWToUOrAXzC/', gradient: 'from-blue-600 via-indigo-500 to-violet-500', emoji: '🎸' },
    { hook: '$999 car-hacking device', views: '61,511', likes: '1,672', url: 'https://www.instagram.com/p/DY4wiVAhftZ/', gradient: 'from-red-600 via-rose-500 to-pink-400', emoji: '🚗' },
];

const YT_TOP = [
    { hook: 'Rapido zero-commission food', views: '35,447', url: 'https://www.youtube.com/shorts/CldWXV4xhEo', gradient: 'from-red-600 via-rose-500 to-orange-400', emoji: '🛵' },
    { hook: 'Your WiFi is spying', views: '28,732', url: 'https://www.youtube.com/shorts/Tsfx3VY7in8', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', emoji: '📡' },
    { hook: "Anthropic's AI team", views: '17,904', url: 'https://www.youtube.com/shorts/ztKzX3tC1uQ', gradient: 'from-amber-600 via-yellow-500 to-orange-400', emoji: '🧠' },
    { hook: 'India tried to build OpenAI', views: '16,399', url: 'https://www.youtube.com/shorts/d29myWCe0f4', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', emoji: '🇮🇳' },
    { hook: 'AI agents run your Workspace', views: '14,292', url: 'https://www.youtube.com/shorts/LLdXpZsPG6k', gradient: 'from-blue-600 via-indigo-500 to-violet-500', emoji: '🤖' },
];

/* ---------------------------------------------------------------- count-up hook */
const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, prefix = '', subtext }) => {
    const [animatedValue, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[170px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight">
                    {prefix}{animatedValue.toLocaleString()}
                </p>
                <p className="text-white/50 text-sm mt-3">{subtext}</p>
            </div>
        </GlassCard>
    );
};

/* Section label with platform icon */
const PlatformLabel = ({ icon: Icon, color, name, tagline }) => (
    <div className="text-center mb-10">
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${color.border} ${color.bg} backdrop-blur-md mb-4`}>
            <Icon size={15} className={color.text} />
            <span className={`text-xs uppercase tracking-widest font-semibold ${color.text}`}>{name}</span>
        </div>
        {tagline && <p className="text-white/50 max-w-2xl mx-auto">{tagline}</p>}
    </div>
);

/* ----------------------------------------------------------------- Hero section */
const Hero = () => (
    <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
        <div className="space-y-6 max-w-5xl mx-auto w-full">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-md mb-2">
                <span className="text-sm font-medium text-yellow-300 tracking-wide uppercase">Mar 10 – Jun 9, 2026 · Last 3 Months</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-yellow-100 to-white drop-shadow-lg leading-tight">
                Kush Content<br />Performance Report
            </h1>

            <p className="text-lg md:text-xl text-yellow-200/70 max-w-2xl mx-auto leading-relaxed">
                Every reel and short across <span className="text-white font-semibold">Instagram + YouTube</span>, measured. The wins, the misses, and the month-by-month arc on both platforms.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12 w-full">
                <KPICard icon={Eye} iconColor="bg-blue-500/20 text-blue-300" label="Total Views" value={COMBINED.views} subtext="Across both platforms" />
                <KPICard icon={Instagram} iconColor="bg-rose-500/20 text-rose-300" label="Instagram Views" value={IG.views} subtext={`${IG.avg.toLocaleString()} avg · ${IG.median.toLocaleString()} median`} />
                <KPICard icon={Youtube} iconColor="bg-red-500/20 text-red-300" label="YouTube Views" value={YT.views} subtext={`${YT.avg.toLocaleString()} avg · ${YT.median.toLocaleString()} median`} />
            </div>

            <p className="text-white/30 text-xs max-w-2xl mx-auto pt-6">
                Figures are public view/play counts, not private "reach". Captured via automated scrape on Jun 10, 2026.
            </p>
        </div>
    </section>
);

/* ------------------------------------------------------ Month-by-month progression */
const fmtK = (n) => (n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);

const ChartTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-black/80 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-left">
            <p className="text-white font-bold text-sm mb-2">{d.label}{d.partial ? ' (partial)' : ''}</p>
            <p className="text-yellow-300 text-sm">Instagram · {d.ig.toLocaleString()} views <span className="text-white/40">({d.igReels} reels)</span></p>
            <p className="text-red-300 text-sm mt-1">YouTube · {d.yt.toLocaleString()} views <span className="text-white/40">({d.ytVids} shorts)</span></p>
        </div>
    );
};

const MonthlyProgression = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                    <BarChart3 size={14} className="text-yellow-300" />
                    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Progression · Both Platforms</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">Month by Month</h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                    Instagram spikes on breakout reels; YouTube holds a steadier baseline. In April and June, YouTube actually out-viewed Instagram.
                </p>
            </div>

            <GlassCard className="mb-10">
                <div className="h-[340px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={MONTHS} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="igFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.45} />
                                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="ytFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#f87171" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={fmtK} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }} axisLine={false} tickLine={false} width={48} />
                            <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
                            <Legend wrapperStyle={{ paddingTop: 8 }} iconType="circle" formatter={(v) => <span className="text-white/60 text-xs">{v === 'ig' ? 'Instagram' : 'YouTube'}</span>} />
                            <Area type="monotone" dataKey="ig" stroke="#fbbf24" strokeWidth={3} fill="url(#igFill)" dot={{ r: 4, fill: '#fbbf24', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                            <Area type="monotone" dataKey="yt" stroke="#f87171" strokeWidth={3} fill="url(#ytFill)" dot={{ r: 4, fill: '#f87171', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <p className="text-white/30 text-xs text-center mt-4">Total monthly views per platform. June is a partial month (through Jun 9).</p>
            </GlassCard>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {MONTHS.map((m) => {
                    const total = m.ig + m.yt;
                    return (
                        <GlassCard key={m.month} className={m.partial ? 'opacity-90' : ''}>
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-center gap-2">
                                    <Calendar size={15} className="text-white/40" />
                                    <h3 className="text-base font-bold text-white">{m.label}</h3>
                                </div>
                                {m.partial && (
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 bg-white/5 px-2 py-0.5 rounded-full border border-white/10">Partial</span>
                                )}
                            </div>
                            <div className="mb-4">
                                <p className="text-white/40 text-xs uppercase tracking-widest font-semibold mb-1">Combined Views</p>
                                <p className="text-3xl font-black text-white">{total.toLocaleString()}</p>
                            </div>
                            <div className="space-y-2.5 pt-3 border-t border-white/5">
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-1.5 text-xs text-yellow-300"><Instagram size={12} /> Instagram</span>
                                    <span className="text-white/80 text-sm font-semibold">{m.ig.toLocaleString()}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-1.5 text-xs text-red-300"><Youtube size={12} /> YouTube</span>
                                    <span className="text-white/80 text-sm font-semibold">{m.yt.toLocaleString()}</span>
                                </div>
                            </div>
                        </GlassCard>
                    );
                })}
            </div>
        </div>
    </section>
);

/* ------------------------------------------------ Cross-platform comparison */
const Compare = () => {
    const rows = [
        { metric: 'Total views', ig: IG.views.toLocaleString(), yt: YT.views.toLocaleString() },
        { metric: 'Average / post', ig: IG.avg.toLocaleString(), yt: YT.avg.toLocaleString() },
        { metric: 'Median / post', ig: IG.median.toLocaleString(), yt: YT.median.toLocaleString(), highlight: true },
    ];
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-4xl">
                <div className="text-center mb-10">
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">Instagram vs YouTube</h2>
                    <p className="text-white/50 max-w-2xl mx-auto">
                        Instagram owns the <span className="text-yellow-300 font-semibold">ceiling</span> (viral spikes up to 160K). YouTube owns the <span className="text-red-300 font-semibold">floor</span> — a 3x higher median means far fewer posts that go nowhere.
                    </p>
                </div>
                <GlassCard>
                    <div className="grid grid-cols-3 gap-2 pb-4 mb-2 border-b border-white/10">
                        <span className="text-white/40 text-xs uppercase tracking-widest font-semibold">Metric</span>
                        <span className="text-yellow-300 text-xs uppercase tracking-widest font-semibold text-right flex items-center justify-end gap-1.5"><Instagram size={13} /> Instagram</span>
                        <span className="text-red-300 text-xs uppercase tracking-widest font-semibold text-right flex items-center justify-end gap-1.5"><Youtube size={13} /> YouTube</span>
                    </div>
                    {rows.map((r) => (
                        <div key={r.metric} className={`grid grid-cols-3 gap-2 py-3 ${r.highlight ? 'bg-white/[0.03] rounded-xl px-2 -mx-2' : ''}`}>
                            <span className="text-white/70 text-sm flex items-center">{r.metric}{r.highlight && <span className="ml-2 text-[10px] text-emerald-300 bg-emerald-500/15 px-1.5 py-0.5 rounded-full">key</span>}</span>
                            <span className="text-white font-bold text-right">{r.ig}</span>
                            <span className="text-white font-bold text-right">{r.yt}</span>
                        </div>
                    ))}
                    <p className="text-white/40 text-xs mt-5 pt-4 border-t border-white/5 leading-relaxed">
                        Same content, two very different shapes. The play: keep Instagram for reach upside, lean on YouTube for a dependable baseline, and reverse-engineer why the handful of cross-platform breakouts traveled.
                    </p>
                </GlassCard>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------- View distribution (IG) */
const Distribution = () => (
    <section className="pt-16 px-4">
        <div className="container mx-auto max-w-5xl">
            <PlatformLabel
                icon={Instagram}
                color={{ border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-300' }}
                name="Instagram · Deep Dive"
                tagline="A hit-driven account. 5 reels (6%) drove most of the views, while half landed under 1,000. That gap is the opportunity: lift the floor, repeat what makes the hits travel."
            />
            <h3 className="text-2xl font-bold text-white mb-6 text-center">The Shape of 80 Reels</h3>
            <GlassCard>
                <div className="space-y-5">
                    {IG_DISTRIBUTION.map((d) => (
                        <div key={d.bucket}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-baseline gap-3">
                                    <span className="text-white font-bold text-sm w-20">{d.bucket}</span>
                                    <span className="text-white/40 text-xs">{d.sub}</span>
                                </div>
                                <span className="text-white/70 text-sm font-mono">{d.count} reels · {d.pct}%</span>
                            </div>
                            <div className="h-3 w-full rounded-full bg-white/5 overflow-hidden">
                                <div className={`h-full rounded-full bg-gradient-to-r ${d.color}`} style={{ width: `${Math.max(d.pct, 2)}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            </GlassCard>
        </div>
    </section>
);

/* ------------------------------------------------------------ reel/video tiles */
const ReelTile = ({ reel, rank, footerSub }) => (
    <a
        href={reel.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03]"
        style={{ aspectRatio: '9/16' }}
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-5xl mb-3 opacity-30 group-hover:opacity-0 transition-opacity duration-300">{reel.emoji}</span>
        </div>
        <div className="absolute top-1/3 left-3 right-3">
            <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.hook}</p>
            </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <Play size={24} className="text-white ml-1" fill="white" />
                </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm font-bold">{reel.views}</p>
            {footerSub && <p className="text-white/50 text-[11px] font-mono">{footerSub(reel)}</p>}
        </div>
        <div className="absolute top-3 left-3">
            <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">#{rank}</span>
        </div>
    </a>
);

const IGTopReels = () => (
    <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">Top 5 Reels — Instagram</h3>
            <p className="text-white/50 text-center mb-10 max-w-xl mx-auto">
                Every breakout is one specific, surprising, news-pegged story. Tap any card to watch.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {IG_TOP.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} footerSub={(r) => `${r.likes} likes`} />)}
            </div>
        </div>
    </section>
);

/* ----------------------------------------------------------- YouTube deep dive */
const YouTubeSection = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <PlatformLabel
                icon={Youtube}
                color={{ border: 'border-red-500/30', bg: 'bg-red-500/10', text: 'text-red-300' }}
                name="YouTube · Deep Dive"
                tagline="The same shorts, a steadier surface. Higher median, fewer flops, and the per-platform winners differ — Rapido topped YouTube but barely moved on Instagram."
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
                <KPICard icon={Play} iconColor="bg-red-500/20 text-red-300" label="Shorts Posted" value={YT.posts} subtext="Last 3 months" />
                <KPICard icon={Eye} iconColor="bg-blue-500/20 text-blue-300" label="Total Views" value={YT.views} subtext={`${YT.avg.toLocaleString()} avg / short`} />
                <KPICard icon={TrendingUp} iconColor="bg-emerald-500/20 text-emerald-300" label="Median Views" value={YT.median} subtext="3x Instagram's median" />
                <KPICard icon={Flame} iconColor="bg-amber-500/20 text-amber-300" label="Best Short" value={35447} subtext="Rapido food delivery" />
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">Top 5 Shorts — YouTube</h3>
            <p className="text-white/50 text-center mb-10 max-w-xl mx-auto">Tap any card to watch.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {YT_TOP.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} />)}
            </div>
        </div>
    </section>
);

/* ----------------------------------------------------------- Takeaway strip */
const Takeaways = () => {
    const items = [
        { stat: 'IG spikes', label: '5 of 80 reels drove most Instagram views. Reverse-engineer those news pegs.' },
        { stat: 'YT floor', label: '3x higher median on YouTube. The same content travels more reliably there.' },
        { stat: 'April dip', label: 'most posts, lowest yield on both platforms. Volume alone did not buy reach.' },
    ];
    return (
        <section className="py-16 px-4">
            <div className="container mx-auto max-w-5xl">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">What the Numbers Say</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map((it, i) => (
                        <GlassCard key={i} className="text-center">
                            <p className="text-2xl font-black text-yellow-300 mb-3">{it.stat}</p>
                            <p className="text-white/70 text-sm leading-relaxed">{it.label}</p>
                        </GlassCard>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------------- Page shell */
export default function KushReport7() {
    useEffect(() => {
        const pageTitle = 'Kush – 3-Month Content Report (IG + YouTube) | SocialSEO';
        document.title = pageTitle;
        const ensureMeta = (selector, attributes) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            el.setAttribute('content', pageTitle);
        };
        ensureMeta('meta[property="og:title"]', { property: 'og:title' });
        ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
            `}</style>

            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-yellow-300">
                        <span className="text-white">Social</span>SEO
                    </span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">Mar 10 – Jun 9, 2026</span>
                </div>
            </header>

            <div className="relative z-10">
                <main>
                    <Hero />
                    <MonthlyProgression />
                    <Compare />
                    <Distribution />
                    <IGTopReels />
                    <YouTubeSection />
                    <Takeaways />
                </main>
                <Footer />
            </div>
        </div>
    );
}
